import React, { useState, useEffect, useCallback } from 'react';
import { useFirebase } from '../../context/firebaseContext';
import { useAuth } from '../../context/authContext';
import { doc, getDoc } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { BookOpen, GraduationCap, Hammer, Calendar, UserCircle2, NotebookPen } from 'lucide-react';

const DashboardComponentModern = () => {
    const { db } = useFirebase();
    const { user: currentUser, loading: authLoading } = useAuth(); 
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchUserData = useCallback(async () => {
        if (!currentUser || !currentUser.uid) {
            // Guest experience: skip fetch and stop loading without redirecting
            setUserData(null);
            setError(null);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const userDocRef = doc(db, 'users', currentUser.uid);
            const userDoc = await getDoc(userDocRef);

            if (userDoc.exists()) {
                setUserData(userDoc.data());
                setError(null);
            } else {
                setError('User not found');
            }
        } catch (err) {
            setError('Error fetching user data');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [currentUser, db]);

    useEffect(() => {
        if (!authLoading) {
            fetchUserData();
        }
    }, [authLoading, fetchUserData]);

    const capitalizeFirstLetter = (value) => {
        const str = value || '';
        return str ? str.charAt(0).toUpperCase() + str.slice(1) : '';
    };

    const name =
        capitalizeFirstLetter(userData?.firstName) ||
        capitalizeFirstLetter(userData?.username) ||
        capitalizeFirstLetter(currentUser?.displayName) ||
        '';

    const now = new Date();
    const formattedDate = now.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
    });
    const hour = now.getHours();
    const quickActions = [
        {
            icon: GraduationCap,
            title: "Academy",
            path: "/mobile/academy",
            size: "wide",
            tone: "amber",
        },
        {
            icon: BookOpen,
            title: "Study",
            path: "/study",
            tone: "copper",
        },
        {
            icon: Hammer,
            title: "Tools",
            path: "/tools",
            tone: "ember",
        },
        {
            icon: NotebookPen,
            title: "Journal",
            path: "/mobile/journal",
            tone: "ink",
        },
        {
            icon: Calendar,
            title: "Calendar",
            path: "/calendar",
            size: "wide",
            tone: "sage",
        },
        {
            icon: UserCircle2,
            title: "Profile",
            path: "/mobile/profile",
            tone: "opal",
        }
    ];

    if (authLoading || loading) {
        return (
            <div className="mobile-dashboard-modern">
                <div className="dashboard-backdrop" aria-hidden="true"></div>
                <div className="dashboard-state">
                    <div className="loading-spinner">
                        <div className="spinner"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="mobile-dashboard-modern">
                <div className="dashboard-backdrop" aria-hidden="true"></div>
                <div className="dashboard-state">
                    <div className="dashboard-error">Error: {error}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="mobile-dashboard-modern">
            <div className="dashboard-backdrop" aria-hidden="true"></div>
            <section className="dashboard-hero">
                <div className="hero-top">
                    <span className="hero-kicker">Dashboard</span>
                    <span className="hero-date">{formattedDate}</span>
                </div>
                <h1 className="hero-title">
                    {name ? `Welcome back, ${name}.` : 'Welcome back.'}
                </h1>
                <div className="hero-actions">
                    <Link to="/academy" className="hero-action primary">Start a lesson</Link>
                    <Link to="/tools" className="hero-action secondary">Open tools</Link>
                </div>
            </section>

            <section className="dashboard-section">
                <div className="section-header">
                        <div>
                            <h2 className="dashboard-section-title">Quick Actions</h2>
                        </div>
                </div>
                <div className="actions-grid">
                    {quickActions.map((action, index) => {
                        const IconComponent = action.icon;
                        const cardClassName = [
                            "action-card",
                            action.size === "wide" && "is-wide",
                            action.tone && `tone-${action.tone}`,
                        ]
                            .filter(Boolean)
                            .join(" ");

                        return (
                            <Link key={index} to={action.path} className={cardClassName}>
                                <div className="action-icon">
                                    <IconComponent size={22} strokeWidth={1.6} className="action-icon-svg" />
                                </div>
                                <div className="action-content">
                                    <div className="action-title">{action.title}</div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </section>
        </div>
    );
};

export default DashboardComponentModern; 
